// ============================================
// Settings — Admin Panel
// Platform configuration and user management
// ============================================

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import AdminLayout from '../../components/admin/AdminLayout';
import adminService from '../../services/adminService';

// Premium SVG Icons
const icons = {
    general: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.341 3.612l.135-1.08c.045-.36.315-.65.666-.75a18.2 18.2 0 014.28 0c.35.1.62.39.666.75l.135 1.08a8.31 8.31 0 001.69 1.01l1.011-.4c.34-.135.731-.059 1.002.196a18.176 18.176 0 012.164 2.164c.25.27.33.66.195 1.003l-.4 1.01a8.31 8.31 0 001.01 1.69l1.08.136c.36.045.65.315.75.666a18.2 18.2 0 010 4.28c-.1.35-.39.62-.75.666l-1.08.135a8.31 8.31 0 00-1.01 1.69l.4 1.011c.135.34.059.731-.196 1.002a18.176 18.176 0 01-2.164 2.164c-.27.25-.66.33-1.003.195l-1.01-.4a8.31 8.31 0 00-1.69 1.01l-.136 1.08c-.045.36-.315.65-.666.75a18.2 18.2 0 01-4.28 0c-.35-.1-.62-.39-.666-.75l-.135-1.08a8.31 8.31 0 00-1.69-1.01l-1.011.4c-.34.135-.731.059-1.002-.196a18.176 18.176 0 01-2.164-2.164c-.25-.27-.33-.66-.195-1.003l.4-1.01a8.31 8.31 0 00-1.01-1.69l-1.08-.136c-.36-.045-.65-.315-.75-.666a18.2 18.2 0 010-4.28c.1-.35.39-.62.75-.666l1.08-.135a8.31 8.31 0 001.01-1.69l-.4-1.011c-.135-.34-.059-.731.196-1.002a18.176 18.176 0 012.164-2.164c.27-.25.66-.33 1.003-.195l1.01.4a8.31 8.31 0 001.69-1.01l.136-1.08zM15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    notifications: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" /></svg>,
    security: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>,
    account: <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>,
};


const InputField = ({ label, value, onChange, type = 'text', disabled = false }) => (
    <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1">{label}</label>
        <input type={type} value={value || ''} onChange={onChange} disabled={disabled}
            className={`w-full px-5 py-3.5 rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-transparent dark:border-slate-700/60 text-slate-900 dark:text-white text-sm font-medium focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all outline-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} />
    </div>
);

const ToggleField = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 hover:bg-white dark:hover:bg-slate-800 transition-all duration-300">
        <div>
            <p className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight">{label}</p>
            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider">{description}</p>
        </div>
        <button onClick={onChange}
            className={`relative w-12 h-6 rounded-full transition-all duration-500 shrink-0 shadow-inner ${checked ? 'bg-gradient-to-r from-indigo-500 to-blue-500 shadow-indigo-500/30' : 'bg-slate-300 dark:bg-slate-700'}`}>
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${checked ? 'left-[26px]' : 'left-0.5'}`} />
        </button>
    </div>
);

const Settings = () => {
    const { user } = useSelector((state) => state.auth);
    const [activeSection, setActiveSection] = useState('notifications');
    const [saved, setSaved] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const showError = (msg) => {
        setErrorMsg(msg);
        setTimeout(() => setErrorMsg(null), 5000);
    };

    const [settings, setSettings] = useState({
        companyName: '',
        companyEmail: '',
        companyPhone: '',
        timezone: 'Europe/London',
        currency: 'GBP',
        dateFormat: 'DD/MM/YYYY',
        notifyDriverExpiry: true,
        notifyMOTExpiry: true,
        notifyNewBooking: true,
        emailNotifications: true,
        smsNotifications: false,
        sessionTimeout: '30',
        twoFactorAuth: false,
        autoLogout: true,
    });

    const [account, setAccount] = useState({
        name: user?.name || '',
        email: user?.email || ''
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await adminService.getSettings();
                if (res.data?.success && res.data?.data?.settings) {
                    setSettings(res.data.data.settings);
                }
            } catch (err) {
                console.error("Error fetching settings:", err);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async () => {
        try {
            if (activeSection === 'account') {
                await adminService.updateProfile(account);
            } else {
                await adminService.updateSettings(settings);
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
            
            if (activeSection === 'account' && (account.name !== user.name || account.email !== user.email)) {
                setTimeout(() => window.location.reload(), 1000); // Reload to reflect Redux changes
            }
        } catch (err) {
            console.error("Error saving settings:", err);
            showError(err.response?.data?.message || 'Failed to save settings. Please try again.');
        }
    };

    const sections = [
        { key: 'notifications', label: 'NOTIFICATIONS', icon: icons.notifications },
        { key: 'security', label: 'SECURITY', icon: icons.security },
        { key: 'account', label: 'PROFILE', icon: icons.account },
    ];



    return (
        <AdminLayout title="Settings" subtitle="Platform parameters and settings">
            <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-6">
                
                {/* Sidebar */}
                <div className="w-full lg:w-72 flex-shrink-0">
                    <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/5 overflow-hidden shadow-sm p-3">
                        <div className="p-3 pb-4">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Settings Categories</p>
                        </div>
                        <div className="space-y-1">
                        {sections.map(s => (
                            <button key={s.key} onClick={() => setActiveSection(s.key)}
                                className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-300 ${activeSection === s.key
                                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-xl shadow-indigo-500/25 active:scale-95'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}>
                                <span className={activeSection === s.key ? 'text-white' : 'text-slate-400 dark:text-slate-500'}>{s.icon}</span>
                                {s.label}
                            </button>
                        ))}
                        </div>
                    </div>
                </div>

                {/* Configuration Content Studio */}
                <div className="flex-1">
                    <div className="bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/5 shadow-sm overflow-hidden flex flex-col h-full min-h-[600px]">
                        
                        <div className="flex-1 p-6 md:p-8 lg:p-10">

                            {/* ── Inline Error Banner ── */}
                            {errorMsg && (
                                <div className="mb-6 flex items-start gap-3 px-5 py-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-rose-700 dark:text-rose-300">Action Failed</p>
                                        <p className="text-[13px] text-rose-600 dark:text-rose-400 mt-0.5 leading-relaxed">{errorMsg}</p>
                                    </div>
                                    <button onClick={() => setErrorMsg(null)} className="p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-400 dark:text-rose-500 transition-colors flex-shrink-0">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            )}

                            {/* Notifications */}
                            {activeSection === 'notifications' && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Notifications</h3>
                                        <p className="text-[11px] font-bold text-indigo-500 dark:text-amber-500 uppercase tracking-widest mt-2 bg-indigo-500/10 dark:bg-amber-500/10 inline-block px-3 py-1 rounded-lg">Configure automated system notifications</p>
                                    </div>
                                    <div className="space-y-4 bg-white/50 dark:bg-slate-800/30 p-6 rounded-[2rem] border border-slate-200/50 dark:border-slate-700/50">
                                        <ToggleField label="Driver License Expiry" description="Receive notification when driver licenses expire within 30 days"
                                            checked={settings.notifyDriverExpiry} onChange={() => setSettings({ ...settings, notifyDriverExpiry: !settings.notifyDriverExpiry })} />
                                        <ToggleField label="Vehicle MOT Expiry" description="Receive notification when vehicle MOT certificates approach expiration"
                                            checked={settings.notifyMOTExpiry} onChange={() => setSettings({ ...settings, notifyMOTExpiry: !settings.notifyMOTExpiry })} />
                                        <ToggleField label="New Private Booking Alerts" description="Receive notification when a new private booking is created"
                                            checked={settings.notifyNewBooking} onChange={() => setSettings({ ...settings, notifyNewBooking: !settings.notifyNewBooking })} />
                                        <ToggleField label="Email Notifications" description="Receive non-critical alerts via email"
                                            checked={settings.emailNotifications} onChange={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })} />
                                        <ToggleField label="SMS Notifications" description="Receive high priority alerts via SMS"
                                            checked={settings.smsNotifications} onChange={() => setSettings({ ...settings, smsNotifications: !settings.smsNotifications })} />
                                    </div>
                                </div>
                            )}

                            {/* Security */}
                            {activeSection === 'security' && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Security Settings</h3>
                                        <p className="text-[11px] font-bold text-indigo-500 dark:text-amber-500 uppercase tracking-widest mt-2 bg-indigo-500/10 dark:bg-amber-500/10 inline-block px-3 py-1 rounded-lg">Authentication and authorization settings</p>
                                    </div>
                                    <div className="space-y-4 bg-white/50 dark:bg-slate-800/30 p-6 rounded-[2rem] border border-slate-200/50 dark:border-slate-700/50">
                                        <div className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                                            <label className="block text-sm font-black text-slate-900 dark:text-white uppercase tracking-tight mb-1">Session Timeout (Minutes)</label>
                                            <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-4 uppercase tracking-wider">Automatically log out when inactive</p>
                                            <select value={settings.sessionTimeout} onChange={e => setSettings({ ...settings, sessionTimeout: e.target.value })}
                                                className="w-full sm:w-64 px-5 py-3.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all outline-none text-slate-900 dark:text-white font-black text-sm">
                                                <option value="15">15 MINUTES</option>
                                                <option value="30">30 MINUTES</option>
                                                <option value="60">1 HOUR</option>
                                                <option value="120">2 HOURS</option>
                                            </select>
                                        </div>
                                        <ToggleField label="Auto-Logout" description="Automatically log out inactive sessions"
                                            checked={settings.autoLogout} onChange={() => setSettings({ ...settings, autoLogout: !settings.autoLogout })} />
                                    </div>
                                </div>
                            )}

                            {/* My Account */}
                            {activeSection === 'account' && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">My Profile</h3>
                                        <p className="text-[11px] font-bold text-indigo-500 dark:text-amber-500 uppercase tracking-widest mt-2 bg-indigo-500/10 dark:bg-amber-500/10 inline-block px-3 py-1 rounded-lg">Personal account settings</p>
                                    </div>
                                    
                                    <div className="bg-white/50 dark:bg-slate-800/30 p-6 rounded-[2rem] border border-slate-200/50 dark:border-slate-700/50">
                                        <div className="flex items-center gap-6 p-6 rounded-[1.5rem] bg-gradient-to-r from-slate-100 to-white dark:from-slate-800 dark:to-slate-800/50 border border-slate-200 dark:border-slate-700 mb-8 shadow-inner">
                                            <div className="w-16 h-16 rounded-[1rem] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-black shadow-xl shadow-indigo-500/30">
                                                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                                            </div>
                                            <div>
                                                <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase">{user?.name || 'Admin'}</p>
                                                <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mt-0.5">{user?.email || 'OFFLINE'}</p>
                                                <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-lg text-[9px] font-black bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 uppercase tracking-widest border border-indigo-500/20">
                                                    ROLE: {user?.role || 'ADMIN'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <InputField label="Name" value={account.name} onChange={e => setAccount({ ...account, name: e.target.value })} />
                                            <InputField label="Email" value={account.email} onChange={e => setAccount({ ...account, email: e.target.value })} />
                                            <InputField label="Role" value={user?.role?.toUpperCase() || 'ADMIN'} disabled />
                                            <InputField label="Member Since" value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB') : 'UNKNOWN'} disabled />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-6 md:p-8 border-t border-slate-200/50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-end gap-5">
                            {saved && (
                                <span className="text-[11px] font-black tracking-widest text-emerald-600 dark:text-emerald-400 uppercase animate-in slide-in-from-right-4 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/> Settings Saved
                                </span>
                            )}
                            <button onClick={handleSave}
                                className="px-8 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 dark:from-amber-600 dark:to-orange-500 dark:hover:from-amber-500 dark:hover:to-orange-400 shadow-xl shadow-indigo-500/25 dark:shadow-orange-500/25 active:scale-95 transition-all">
                                SAVE SETTINGS
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default Settings;
